import React, { lazy, Suspense } from 'react'

import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom"
import { CompatRoute, CompatRouter } from 'react-router-dom-v5-compat'
import { Card, Spin } from 'antd'

import authStorage from '../services/authStorage'
import { PriaChatTour } from '../components/Tours/PriaChatTour'
import { PriaModalTour } from '../components/Tours/PriaModalTour'

const Layout = lazy(() => import('../components/layout/LayoutDefault'))
const DashboardMain = lazy(() => import('../pages/dashboards/DashboardMain'))
const ValueDashboard = lazy(() => import('../pages/dashboards/ValueDashboard'))
const HealthWindow = lazy(() => import('../pages/dashboards/HealthWindow'))
const SeverityData = lazy(() => import('../pages/dashboards/SeverityData'))
const ScheduledScanDetails = lazy(() => import('../pages/scans/ScheduledScanDetails'))
const BatchScheduledDetails = lazy(() => import('../pages/scans/BatchScheduledDetails'))
const DashboardScan = lazy(() => import('../pages/scans/DashboardScan'))
const ParametersScan = lazy(() => import('../pages/scans/ParametersScan'))
const ManageScanList = lazy(() => import('../pages/scans/ManageScanList'))
const AgentList = lazy(() => import('../pages/scans/AgentList'))
const AgentErrorDirectoriesList = lazy(() => import('../pages/scans/AgentErrorDirectoriesList'))
const CategoryList = lazy(() => import('../pages/detectors/CategoryList'))
const CategoryEdit = lazy(() => import('../pages/detectors/CategoryEdit'))
const ClassEdit = lazy(() => import('../pages/detectors/ClassEdit'))
const ClassList = lazy(() => import('../pages/detectors/ClassList'))
const ClassRuleEdit = lazy(() => import('../pages/detectors/ClassRuleEdit'))
const EnginesList = lazy(() => import('../pages/detectors/EngineList'))
const UserList = lazy(() => import('../pages/settings/UserList'))
const UserEdit = lazy(() => import('../pages/settings/UserEdit'))
const ScanResultObjectsList = lazy(() => import('../pages/scans/ScanResultObjectsList'))
const License = lazy(() => import('../pages/settings/License'))
const AgentsDownloads = lazy(() => import('../pages/settings/AgentsDownload'))
const AccessKeys = lazy(() => import('../pages/settings/AccessKeys'))
const ManageSearches = lazy(() => import('../pages/scans/ManageSearches'))
const ExclusionsList = lazy(() => import('../pages/detectors/ExclusionList'))
const HolderDetail = lazy(() => import('../pages/scans/HolderDetail'))
const HolderAssociatedData = lazy(() => import('../pages/scans/HolderAssociatedData'))
const MappingDataBaseList = lazy(() => import('../pages/detectors/MappingDatabaseList'))
const MappingDataBaseEdit = lazy(() => import('../pages/detectors/MappingDatabaseEdit'))
const DataTree = lazy(() => import('../pages/reports/DataTree'))
const CustomQueryList = lazy(() => import('../pages/settings/CustomQueryList'))
const CustomQueryEdit = lazy(() => import('../pages/settings/CustomQueryEdit'))
const AutoLoginPrivacy = lazy(() => import('../pages/auth/AutoLoginPrivacy'))
const MicrosoftCallBack = lazy(() => import('../pages/auth/Login/components/callback'))
const OpenIdCallBack = lazy(() => import('../pages/auth/Login/components/OpenIdCallback'))
const AgentConfigList = lazy(() => import('../pages/settings/AgentConfigList'))
const AgentConfigEdit = lazy(() => import('../pages/settings/AgentConfigEdit'))
const CompanyList = lazy(() => import('../pages/settings/CompanyList'))
const CompanyEdit = lazy(() => import('../pages/settings/CompanyEdit'))
const Log = lazy(() => import('../pages/reports/Log'))
const SearchData = lazy(() => import('../pages/scans/SearchData'))
const Integration = lazy(() => import('../pages/settings/Integration'))
const LoginSsoList = lazy(() => import('../pages/settings/LoginSsoList'))
const LoginSsoEdit = lazy(() => import('../pages/settings/LoginSsoEdit'))
const DossierEdit = lazy(() => import('../pages/settings/DossierEdit'))
const ReportEdit = lazy(() => import('../pages/settings/ReportEdit'))
const BackupElastic = lazy(() => import('../pages/settings/BackupElastic'))
const ModelManagement = lazy(() => import('../pages/scans/ModelManagement'))
const DataCatalogList = lazy(() => import('../pages/catalog/DataCatalogList'))
const DataCatalogEdit = lazy(() => import('../pages/catalog/DataCatalogEdit'))
const AccordionUsage = lazy(() => import('../pages/catalog/DataCatalogReport'))
const DataCatalogTag = lazy(() => import('../pages/catalog/DataCatalogTag'))
const SettingsMain = lazy(() => import('../pages/settings/index'))
const AnalyzerList = lazy(() => import('../pages/redaction/analyzerList/index'))
const AnalyzerRead = lazy(() => import('../pages/redaction/analyzerRead/index'))
const AutomaticAnalisyEdit = lazy(() => import('../pages/redaction/AutomaticAnalisyEdit/index'))
const RedactionScanDetails = lazy(() => import('../pages/redaction/RedactionScanDetails/index'))
const ImportCredentials = lazy(() => import('../pages/settings/ImportCredentials'))
const CompareScan = lazy(() => import('../pages/scans/CompareScan'))
const ProfileEdit = lazy(() => import('../pages/settings/Profile/Edit'))
const ProfileList = lazy(() => import('../pages/settings/Profile/List'))
const ClassificationOrder = lazy(() => import('../pages/detectors/ClassificationOrder'))

const Router = () => (
  <BrowserRouter>
    <CompatRouter>
      <>
        <Switch>
          <Suspense fallback={
            <div style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Spin />
            </div>
          }>
            <Route exact path="/auth/session/:tokenPrivacy">
              <AutoLoginPrivacy />
            </Route>
            <Route exact path="/auth/openid/callback" component={OpenIdCallBack} />
            <Route exact path="/auth/microsoft/callback" component={MicrosoftCallBack} />
            <Layout>
              <Suspense fallback={<Card loading />}>
                <Route exact path="/" component={ValueDashboard} />

              <Route exact path="/credentials/import/import-credentials" component={ImportCredentials} />

              <Route exact path="/dashboards/dashboard-value" component={ValueDashboard} />
              <Route exact path="/dashboards/dashboard-main" component={DashboardMain} />
              <Route exact path="/dashboards/health" component={HealthWindow} />
              <Route exact path="/dashboards/severity-data" component={SeverityData} />
              <Route exact path="/dashboards/dashboard" component={DashboardScan} />

              <Route exact path="/scans/manage-scans" component={ManageScanList} />
              <Route exact path="/scans/agents-scans" component={AgentList} />
              <Route exact path="/scans/agents-scans/:id/directories" component={AgentErrorDirectoriesList} />
              <Route exact path="/scans/parameters" component={ParametersScan} />
              <Route exact path="/scans/search" component={ManageSearches} />
              <Route exact path="/scans/search/:holder_uuid/list" component={HolderDetail} />
              <Route exact path="/scans/search/:holder_uuid/associated-data/list" component={HolderAssociatedData} />
              <Route exact path="/scans/model-management/:id" component={ModelManagement} />
              <Route exact path="/scans/searchdata/:id" component={SearchData} />
              <Route exact path="/scans/:resultId/objects" component={ScanResultObjectsList} />
              <Route exact path="/scans/manage-scans/compare/:idscan" component={CompareScan}/>
              <Route exact path="/scans/manage-scans/details/:scanId" component={ScheduledScanDetails} />
              <Route exact path="/scans/manage-scans/batch/:scanId" component={BatchScheduledDetails} />

              <Route exact path="/detectors/classification-order" component={ClassificationOrder} />
              <Route exact path="/detectors/categories" component={CategoryList} />
              <Route exact path="/detectors/categories/new" component={CategoryEdit} />
              <Route exact path="/detectors/categories/:id/edit" component={CategoryEdit} />
              <Route exact path="/detectors/classes/new" component={ClassEdit} />
              <Route exact path="/detectors/classes/:id/edit" component={ClassEdit} />
              <Route exact path="/detectors/classes" component={ClassList} />
              <Route exact path="/detectors/classes/:id/rule/edit" component={ClassRuleEdit} />
              <Route exact path="/detectors/engines" component={EnginesList} />
              <Route exact path="/detectors/exclusions" component={ExclusionsList} />
              <Route exact path="/detectors/mapping-database" component={MappingDataBaseList} />
              <Route exact path="/detectors/mapping-database/:id_credencial" component={MappingDataBaseEdit} />

              <Route exact path="/catalog/gerent" component={DataCatalogList} />
              <Route exact path="/catalog/gerent/column" component={DataCatalogEdit} />
              <Route exact path="/catalog/tag" component={DataCatalogTag} />
              <Route exact path="/catalog/report" component={AccordionUsage} />

              <Route exact path="/reports/data-tree" component={DataTree} />
              <Route exact path="/reports/log" component={Log} />

              <Route exact path="/settingsMain/users" component={UserList} />
              <Route exact path="/settingsMain/users/:id/edit" component={UserEdit} />
              <Route exact path="/settingsMain/users/new" component={UserEdit} />
              <Route exact path="/settingsMain/profiles" component={ProfileList} />
              <Route exact path="/settingsMain/profiles/new" component={ProfileEdit} />
              <Route exact path="/settingsMain/profiles/:id/edit" component={ProfileEdit} />
              <Route exact path="/settingsMain/agent-config" component={AgentConfigList} />
              <Route exact path="/settingsMain/agent-config/new" component={AgentConfigEdit} />
              <Route exact path="/settingsMain/agent-config/:id/edit" component={AgentConfigEdit} />
              <Route exact path="/settingsMain/companies" component={CompanyList} />
              <Route exact path="/settingsMain/companies/new" component={CompanyEdit} />
              <Route exact path="/settingsMain/companies/:id/edit" component={CompanyEdit} />
              <Route exact path="/settingsMain/custom-queries" component={CustomQueryList} />
              <Route exact path="/settingsMain/custom-queries/new" component={CustomQueryEdit} />
              <Route exact path="/settingsMain/custom-queries/:id/edit" component={CustomQueryEdit} />
              <Route exact path="/settingsMain/my-license" component={License} />
              <Route exact path="/settingsMain/access-keys" component={AccessKeys} />
              <Route exact path="/settingsMain/agents-download" component={AgentsDownloads} />
              <Route exact path="/settingsMain/integration" component={Integration} />
              <Route exact path="/settingsMain/login-sso" component={LoginSsoList} />
              <Route exact path="/settings/export-config/dossier" component={DossierEdit} />
              <Route exact path="/settings/export-config/report" component={ReportEdit} />
              <Route exact path="/settingsMain/login-sso/new" component={LoginSsoEdit} />
              <Route exact path="/settingsMain/login-sso/:id/edit" component={LoginSsoEdit} />
              
              <Route exact path="/settings/backupElastic" component={BackupElastic} />

              <CompatRoute exact path="/redaction/analyzer" component={AnalyzerList} />
              <Route exact path="/redaction/analyzer/:hashDocument/read" component={AnalyzerRead} />
              <Route exact path="/redaction/analyzer/new" component={AutomaticAnalisyEdit} />
              <Route exact path="/redaction/analyzer/:id/details" component={RedactionScanDetails} />
              <Route exact path="/settingsMain">
                <Redirect to="/settingsMain/myAccount" />
              </Route>
              <Route exact path="/settings">
                <Redirect to="/settingsMain/myAccount" />
              </Route>
                <Route path="/settingsMain/:tab/:id?" component={SettingsMain} />
              </Suspense>
            </Layout>
          </Suspense>
        </Switch>
        {authStorage.isAuthenticated() && (
          <>
            <PriaModalTour />
            <PriaChatTour />
          </>
        )}
      </>
    </CompatRouter>
  </BrowserRouter>
)


export default Router
